import * as React from "react";

import { formatThousands, toCleanNumeric } from "@/lib/currency";

export interface CurrencyInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> {
  /** Clean numeric string stored by the parent (no separators), e.g. "500000"
   * or "500000.50". The component renders it grouped with thousands separators. */
  value: string;
  /** Receives the cleaned value (separators stripped) on every edit, so the
   * parent's existing parseFloat / cents conversion is unchanged. */
  onValueChange: (value: string) => void;
}

/**
 * Currency text input that shows the value with thousands separators as the
 * user types and hands the parent back a clean numeric string (CHP-503).
 *
 * Uses `type="text"` + `inputMode="decimal"` deliberately: a native
 * `type="number"` cannot render commas at all. The parent keeps storing a clean
 * value, so existing validation (`parseFloat(value) > 0`) and the cents
 * conversion work as before — and a comma-formatted figure no longer 422s the
 * closed-won submit (CHP-504).
 */
export function CurrencyInput({ value, onValueChange, ...rest }: CurrencyInputProps) {
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={formatThousands(value)}
      onChange={(e) => onValueChange(toCleanNumeric(e.target.value))}
    />
  );
}
